import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { ErrorNotice, Field, Notice, Spinner, formatDate } from '../components/ui';
import type { EmailTemplate } from '../types';

/**
 * The wording of the automatic reminders, editable by the committee.
 *
 * The list of merge fields comes from the server alongside the templates
 * rather than being written out here, so what a volunteer is told they can
 * use can never drift from what actually works.
 */
export function EmailTemplatesPage() {
  const [templates, setTemplates] = useState<EmailTemplate[] | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState<string | null>(null);

  function load() {
    api
      .get<{ templates: EmailTemplate[]; merge_fields: Record<string, string> }>('/email/templates')
      .then((result) => {
        setTemplates(result.templates);
        setFields(result.merge_fields);
      })
      .catch(setError);
  }

  useEffect(load, []);

  return (
    <>
      <p className="muted small">
        <Link to="/email">← Back to email</Link>
      </p>

      <div className="page-head">
        <h1>Email templates</h1>
      </div>

      {saved ? <Notice kind="success">{saved} saved.</Notice> : null}
      <ErrorNotice error={error} />

      <div className="card">
        <h2>Merge fields</h2>
        <p className="muted small">
          Put any of these in a subject or message and each member gets their own value. A field
          that isn't on this list will be refused when you save, rather than going out wrong.
        </p>
        <dl className="details">
          {Object.entries(fields).map(([field, description]) => (
            <div className="detail" key={field}>
              <dt>
                <code>{`{{${field}}}`}</code>
              </dt>
              <dd>{description}</dd>
            </div>
          ))}
        </dl>
      </div>

      {templates === null && error === null ? <Spinner label="Loading templates" /> : null}

      {templates?.map((template) =>
        editing === template.id ? (
          <TemplateEditor
            key={template.id}
            template={template}
            onCancel={() => setEditing(null)}
            onSaved={(name) => {
              setEditing(null);
              setSaved(name);
              window.setTimeout(() => setSaved(null), 4000);
              load();
            }}
          />
        ) : (
          <div className="card" key={template.id}>
            <div className="page-head card-head">
              <h2>{template.name}</h2>
              <button type="button" onClick={() => setEditing(template.id)}>
                Edit
              </button>
            </div>
            {template.description ? <p className="muted small">{template.description}</p> : null}
            <dl className="details">
              <div className="detail">
                <dt>Subject</dt>
                <dd>{template.subject}</dd>
              </div>
              <div className="detail">
                <dt>Last changed</dt>
                <dd>{formatDate(template.updated_at ?? null)}</dd>
              </div>
            </dl>
            <div className="email-preview" dangerouslySetInnerHTML={{ __html: template.body_html }} />
          </div>
        ),
      )}
    </>
  );
}

function TemplateEditor({
  template,
  onCancel,
  onSaved,
}: {
  template: EmailTemplate;
  onCancel: () => void;
  onSaved: (name: string) => void;
}) {
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body_html);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.put(`/email/templates/${template.id}`, { subject, body_html: body });
      onSaved(template.name);
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card form wide" onSubmit={onSubmit}>
      <h2>{template.name}</h2>
      <ErrorNotice error={error} />
      <Field label="Subject" required value={subject} onChange={(e) => setSubject(e.target.value)} />
      <div className="field">
        <label htmlFor={`body-${template.id}`}>Message</label>
        <textarea
          id={`body-${template.id}`}
          rows={10}
          required
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </div>
      <div className="actions">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save template'}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
